import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, FolderArchive, UploadCloud, Settings as SettingsIcon, LogOut, Scale, Menu, X, FileText, FilePlus2, ReceiptText, ClipboardList, Users, Code2, FolderOpen, QrCode, BookOpenCheck, Calculator, Lightbulb, ScrollText, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import ThemeToggle from "@/components/ThemeToggle";

const nav = [
  { to: "/", label: "Beranda", icon: LayoutDashboard, testid: "nav-beranda", end: true },
  { to: "/arsip", label: "Arsip Dokumen", icon: FolderArchive, testid: "nav-arsip" },
  { to: "/upload", label: "Upload Dokumen", icon: UploadCloud, testid: "nav-upload" },
  { to: "/buat-dokumen", label: "Buat Dokumen", icon: FilePlus2, testid: "nav-buat-dokumen" },
  { to: "/invoice", label: "Invoice", icon: ReceiptText, testid: "nav-invoice" },
  { to: "/pajak", label: "Pajak", icon: Calculator, testid: "nav-pajak" },
  { to: "/pengingat", label: "Pengingat", icon: Lightbulb, testid: "nav-pengingat" },
  { to: "/qr", label: "QR", icon: QrCode, testid: "nav-qr" },
  { to: "/klapper", label: "Buku Klapper", icon: BookOpenCheck, testid: "nav-klapper" },
  { to: "/explorer", label: "Explorer Arsip", icon: FolderOpen, testid: "nav-explorer" },
];

const templateNav = [
  { to: "/template", label: "Persyaratan", icon: FileText, testid: "nav-template" },
  { to: "/template-akta", label: "Akta", icon: ScrollText, testid: "nav-template-akta" },
  { to: "/template-kuitansi", label: "Kuitansi", icon: ReceiptText, testid: "nav-template-kuitansi" },
  { to: "/template-tanda-terima", label: "Tanda Terima", icon: ReceiptText, testid: "nav-template-tanda-terima" },
  { to: "/template-surat-keterangan", label: "Surat Keterangan", icon: FileText, testid: "nav-template-surat-keterangan" },
  { to: "/template-covernote", label: "Covernote", icon: FileText, testid: "nav-template-covernote" },
  { to: "/template-rincian-biaya", label: "Rincian Biaya", icon: ReceiptText, testid: "nav-template-rincian-biaya" },
];

const developerNav = [
  { to: "/developer/logs", label: "Log Developer", icon: ClipboardList, testid: "nav-dev-logs" },
  { to: "/developer/staff", label: "Kelola Staff", icon: Users, testid: "nav-dev-staff" },
  { to: "/dev", label: "Developer", icon: Code2, testid: "nav-dev" },
];

function SidebarContent({ user, initials, handleLogout, onNavClick }) {
  const [templatesOpen, setTemplatesOpen] = useState(true);
  return (
    <>
      <div className="shrink-0 px-5 py-5 flex items-center gap-3 border-b border-white/[0.06]">
        <div className="w-10 h-10 shrink-0 rounded-lg flex items-center justify-center bg-gradient-to-br from-gold to-gold-dark text-ink shadow-lg shadow-black/40">
          <Scale className="w-5 h-5" strokeWidth={1.75} />
        </div>
        <div className="min-w-0 leading-tight">
          <div className="truncate font-display text-lg font-semibold tracking-wide text-white">Arsip Notaris</div>
          <div className="truncate text-[10px] uppercase tracking-[0.22em] text-gold/80">Digital Vault</div>
        </div>
      </div>

      <nav className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-4 space-y-1 sidebar-scroll">
        <div className="px-3 pb-2 text-[10px] uppercase tracking-[0.25em] text-white/30">Menu</div>
        {nav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={n.testid}
            onClick={onNavClick}
            className={({ isActive }) =>
              `group flex items-center gap-3 pl-3 pr-3 py-2.5 text-sm rounded-lg relative transition-all duration-300 hover:translate-x-1 ${
                isActive
                  ? "bg-ink-soft text-gold border-l-2 border-gold"
                  : "text-[#E5E0D8]/70 hover:text-white hover:bg-white/[0.04]"
              }`
            }
          >
            <n.icon className="w-[18px] h-[18px]" strokeWidth={1.6} />
            {n.label}
          </NavLink>
        ))}

        <button type="button" onClick={() => setTemplatesOpen((v) => !v)} className="w-full group flex items-center gap-3 pl-3 pr-3 py-2.5 text-sm rounded-lg text-[#E5E0D8]/70 hover:text-white hover:bg-white/[0.04] transition-all duration-300">
          <FileText className="w-[18px] h-[18px]" strokeWidth={1.6} />
          <span className="flex-1 text-left">Template</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${templatesOpen ? "rotate-180" : ""}`} />
        </button>
        {templatesOpen && (
          <div className="ml-5 pl-3 border-l border-white/10 space-y-1">
            {templateNav.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={n.testid} onClick={onNavClick}
                className={({ isActive }) => `group flex items-center gap-2 px-3 py-2 text-xs rounded-lg relative transition-all duration-300 ${isActive ? "bg-gold/10 text-gold" : "text-[#E5E0D8]/60 hover:text-white hover:bg-white/[0.04]"}`}>
                <n.icon className="w-4 h-4" strokeWidth={1.6} />{n.label}
              </NavLink>
            ))}
          </div>
        )}

        {user?.role === "developer" && (
          <>
            <div className="px-3 pt-5 pb-2 text-[10px] uppercase tracking-[0.25em] text-cyan-300/70 flex items-center gap-2"><Code2 className="w-3 h-3" /> Dev</div>
            {developerNav.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={n.testid} onClick={onNavClick}
                className={({ isActive }) => `group flex items-center gap-3 pl-3 pr-3 py-2.5 text-sm rounded-lg relative transition-all duration-300 hover:translate-x-1 ${isActive ? "bg-cyan-400/10 text-cyan-200 border-l-2 border-cyan-300" : "text-[#E5E0D8]/70 hover:text-white hover:bg-white/[0.04]"}`}>
                <n.icon className="w-[18px] h-[18px]" strokeWidth={1.6} />{n.label}
              </NavLink>
            ))}
          </>
        )}
      </nav>

      <div className="shrink-0 p-3 border-t border-white/[0.06] bg-ink">
        <ThemeToggle />
        <div className="flex items-center gap-3 px-2 py-2.5 mb-1 mt-1">
          <div className="w-9 h-9 shrink-0 rounded-full bg-ink-soft border border-gold/30 flex items-center justify-center text-gold text-xs font-semibold font-mono">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-white truncate" data-testid="user-name">{user?.name}</div>
            <div className="text-[11px] text-white/40 truncate">{user?.email}</div>
            {user?.role === "developer" && <div className="mt-1 inline-flex items-center gap-1 rounded bg-cyan-400/10 px-1.5 py-0.5 text-[9px] font-bold tracking-widest text-cyan-200"><Code2 className="w-2.5 h-2.5" />DEV</div>}
          </div>
        </div>
        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-[#E5E0D8]/60 hover:text-wine hover:bg-white/[0.04] rounded-lg transition-colors"
        >
          <LogOut className="w-[18px] h-[18px]" strokeWidth={1.6} />
          Keluar
        </button>
      </div>
    </>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const initials = (user?.name || "N").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen flex bg-linen">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 bg-ink text-[#E5E0D8] flex-col fixed inset-y-0 left-0 overflow-hidden">
        <SidebarContent user={user} initials={initials} handleLogout={handleLogout} />
      </aside>

      {/* Mobile hamburger button */}
      <button
        onClick={() => setMobileOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-50 w-10 h-10 rounded-lg bg-ink text-gold flex items-center justify-center shadow-lg"
        data-testid="mobile-menu-btn"
        aria-label="Buka menu"
      >
        <Menu className="w-5 h-5" strokeWidth={1.6} />
      </button>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <>
          <div className="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-50" onClick={() => setMobileOpen(false)} />
          <aside className="lg:hidden fixed inset-y-0 left-0 w-72 max-w-[86vw] overflow-hidden bg-ink text-[#E5E0D8] flex flex-col z-50 shadow-2xl">
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute top-4 right-4 text-white/60 hover:text-white"
              aria-label="Tutup menu"
            >
              <X className="w-5 h-5" />
            </button>
            <SidebarContent user={user} initials={initials} handleLogout={handleLogout} onNavClick={() => setMobileOpen(false)} />
          </aside>
        </>
      )}

      <main className="flex-1 lg:ml-64 min-h-screen c-appbg linen-grain">
        <Outlet />
      </main>
    </div>
  );
}
